import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { CalendarAppointment, CalendarColumn, CalendarHours } from './calendar-types';
import { clampMinute, snapMinute } from './calendar-time';

export const TOUCH_HOLD_MS = 150;
const MOVE_THRESHOLD_PX = 4;
const CANCEL_HOLD_PX = 8;

export interface DragTarget {
  columnKey: string;
  dayIndex: number;
  providerId: string;
  startMinute: number;
}

export interface DragState {
  appointment: CalendarAppointment;
  originColumnKey: string;
  originMinute: number;
  target: DragTarget | null;
  moved: boolean;
}

export interface DragOptions {
  hours: CalendarHours;
  pxPerHour: number;
  columns: CalendarColumn[];
  columnElement: (key: string) => HTMLElement | null;
  onDrop: (appointment: CalendarAppointment, target: DragTarget) => void;
  enabled: boolean;
  snap?: (columnKey: string, providerId: string, minute: number) => number;
}

interface Gesture {
  appointment: CalendarAppointment;
  columnKey: string;
  originMinute: number;
  startX: number;
  startY: number;
  pointerId: number;
  element: HTMLElement;
  armed: boolean;
  holdTimer: ReturnType<typeof setTimeout> | null;
}

export function swallowNextClick(target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window): void {
  const swallow = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
  };
  target.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(() => target.removeEventListener('click', swallow, { capture: true }), 0);
}

export function useCalendarDrag(options: DragOptions) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const latest = useRef(options);
  latest.current = options;

  const finish = useCallback(() => {
    const current = gesture.current;
    if (current?.holdTimer) clearTimeout(current.holdTimer);
    if (current && current.element.hasPointerCapture(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
    gesture.current = null;
    setDrag(null);
  }, []);

  const targetAt = useCallback((clientX: number, clientY: number, current: Gesture): DragTarget | null => {
    const { hours, pxPerHour, columns, columnElement, snap } = latest.current;
    let column: CalendarColumn | null = null;
    let rect: DOMRect | null = null;
    for (const candidate of columns) {
      const element = columnElement(candidate.key);
      if (!element) continue;
      const box = element.getBoundingClientRect();
      if (clientX >= box.left && clientX <= box.right) {
        column = candidate;
        rect = box;
        break;
      }
    }
    if (!column || !rect) return null;
    const deltaMinutes = ((clientY - current.startY) / pxPerHour) * 60;
    const length = Math.max(current.appointment.durationMinutes, 10);
    const providerId = column.providerIds.length === 1 ? column.providerIds[0] : current.appointment.providerId;
    const rawMinute = current.originMinute + deltaMinutes;
    const snapped = snap ? snap(column.key, providerId, rawMinute) : snapMinute(rawMinute);
    const startMinute = clampMinute(snapped, hours, length);
    return { columnKey: column.key, dayIndex: column.dayIndex, providerId, startMinute };
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>, appointment: CalendarAppointment, columnKey: string, originMinute: number) => {
      if (!latest.current.enabled || event.button !== 0) return;
      const element = event.currentTarget;
      const current: Gesture = {
        appointment,
        columnKey,
        originMinute,
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        element,
        armed: event.pointerType !== 'touch',
        holdTimer: null,
      };
      if (!current.armed) {
        current.holdTimer = setTimeout(() => {
          current.armed = true;
          current.holdTimer = null;
          setDrag({ appointment, originColumnKey: columnKey, originMinute, target: null, moved: false });
        }, TOUCH_HOLD_MS);
      }
      gesture.current = current;
      element.setPointerCapture(event.pointerId);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const current = gesture.current;
      if (!current || event.pointerId !== current.pointerId) return;
      const dx = Math.abs(event.clientX - current.startX);
      const dy = Math.abs(event.clientY - current.startY);
      if (!current.armed) {
        if (dx + dy > CANCEL_HOLD_PX) finish();
        return;
      }
      const moved = dx + dy > MOVE_THRESHOLD_PX;
      if (!moved && !drag) return;
      const target = targetAt(event.clientX, event.clientY, current);
      setDrag({
        appointment: current.appointment,
        originColumnKey: current.columnKey,
        originMinute: current.originMinute,
        target,
        moved: moved || Boolean(drag?.moved),
      });
    },
    [drag, finish, targetAt],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const current = gesture.current;
      if (!current || event.pointerId !== current.pointerId) return;
      const state = drag;
      finish();
      if (state?.moved) swallowNextClick();
      if (!state?.moved || !state.target) return;
      const unchanged = state.target.columnKey === state.originColumnKey && state.target.startMinute === state.originMinute;
      if (!unchanged) latest.current.onDrop(state.appointment, state.target);
    },
    [drag, finish],
  );

  useEffect(() => {
    if (!drag) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drag, finish]);

  return { drag, onPointerDown, onPointerMove, onPointerUp, onPointerCancel: finish };
}
